import { Box, InputLabel, TextField, Typography } from "@material-ui/core";
import ListSubheader from "@material-ui/core/ListSubheader";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Autocomplete from "@material-ui/lab/Autocomplete";
import PropTypes from "prop-types";
import React from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { VariableSizeList } from "react-window";
import { isNotEmpty } from "../../Utils";

const LISTBOX_PADDING = 8; // px

function renderRow(props) {
    const { data, index, style } = props;
    return React.cloneElement(data[index], {
        style: {
            ...style,
            top: style.top + LISTBOX_PADDING,
        },
    });
}

const OuterElementContext = React.createContext({});
// eslint-disable-next-line react/display-name
const OuterElementType = React.forwardRef((props, ref) => {
    const outerProps = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
    const ref = React.useRef(null);
    React.useEffect(() => {
        if (ref.current != null) {
            ref.current.resetAfterIndex(0, true);
        }
    }, [data]);
    return ref;
}

// Adapter for react-window
const ListboxComponent = React.forwardRef(function ListboxComponent(
    props,
    ref
) {
    const { children, ...other } = props;
    const itemData = React.Children.toArray(children);
    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up("sm"), { noSsr: true });
    const itemCount = itemData.length;
    const itemSize = smUp ? 36 : 48;

    const getChildSize = (child) => {
        if (React.isValidElement(child) && child.type === ListSubheader) {
            return 48;
        }

        return itemSize;
    };

    const getHeight = () => {
        if (itemCount > 8) {
            return 8 * itemSize;
        }
        return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
    };

    const gridRef = useResetCache(itemCount);

    return (
        <div ref={ref}>
            <OuterElementContext.Provider value={other}>
                <VariableSizeList
                    itemData={itemData}
                    height={getHeight() + 2 * LISTBOX_PADDING}
                    width="100%"
                    ref={gridRef}
                    outerElementType={OuterElementType}
                    innerElementType="ul"
                    itemSize={(index) => getChildSize(itemData[index])}
                    overscanCount={5}
                    itemCount={itemCount}
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
        </div>
    );
});

ListboxComponent.propTypes = {
    children: PropTypes.node,
};

// function random(length) {
//     const characters =
//         "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
//     let result = "";

//     for (let i = 0; i < length; i += 1) {
//         result += characters.charAt(
//             Math.floor(Math.random() * characters.length)
//         );
//     }

//     return result;
// }

const useStyles = makeStyles({
    listbox: {
        boxSizing: "border-box",
        "& ul": {
            padding: 0,
            margin: 0,
        },
    },
});

// const OPTIONS = Array.from(new Array(10000))
//     .map(() => random(10 + Math.ceil(Math.random() * 20)))
//     .sort((a, b) => a.toUpperCase().localeCompare(b.toUpperCase()));

const renderGroup = (params) => [
    <ListSubheader key={params.key} component="div">
        {params.group}
    </ListSubheader>,
    params.children,
];

export default function CustomSchoolAutocomplete({
    required = true,
    control,
    schoolNameFieldName,
    label,
    options,
    onChange = () => {},
    hanldFilledCode = () => {},
    displayFullOption = false,
    ...rest
}) {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    // console.log("options====", options);
    return (
        <Controller
            control={control}
            name={schoolNameFieldName}
            render={({ field }) => (
                <Box mb={6}>
                    {label && (
                        <InputLabel shrink htmlFor={schoolNameFieldName}>
                            {label}
                            {t("common.colon")}
                            {required && (
                                <Box component="span" className="danger">
                                    *
                                </Box>
                            )}
                        </InputLabel>
                    )}
                    <Autocomplete
                        id={schoolNameFieldName}
                        // style={{ width: 300 }}
                        disableListWrap
                        classes={classes}
                        ListboxComponent={
                            displayFullOption ? "ul" : ListboxComponent
                        }
                        renderGroup={renderGroup}
                        options={options}
                        // groupBy={(option) => option[0].toUpperCase()}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                style={{ width: "80%" }}
                                // variant="outlined"
                                // label={label}
                            />
                        )}
                        renderOption={(option) => {
                            return displayFullOption ? (
                                <Typography
                                    noWrap={false}
                                    style={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        display: "-webkit-box",
                                        WebkitBoxOrient: "vertical",
                                        //WebkitLineClamp: '3',
                                    }}
                                >
                                    {option}
                                </Typography>
                            ) : (
                                <Typography noWrap>{option}</Typography>
                            );
                        }}
                        onChange={(e, newValue) => {
                            if (isNotEmpty(newValue)) {
                                field.onChange(newValue);
                            } else {
                                field.onChange("");
                            }
                            hanldFilledCode();
                        }}
                        value={field.value}
                        {...rest}
                    />
                </Box>
            )}
        />
    );
}
